import { useMemo, useRef, useState } from "react";
import {
  Lifebuoy,
  MagnifyingGlass,
  RocketLaunch,
  FolderSimple,
  Receipt,
  Sparkle,
  Scales,
  Plugs,
  GearSix,
  X,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import CategoryCards from "../components/help/CategoryCards";

// ─── Taxonomy ───────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: "getting-started", label: "Getting Started", icon: RocketLaunch, description: "Set up your workspace" },
  { id: "projects-files",  label: "Projects & Files", icon: FolderSimple, description: "Manage projects, tasks & OneDrive" },
  { id: "financials",      label: "Financials",       icon: Receipt,      description: "Invoices & cost tracking" },
  { id: "ai-automations",  label: "AI & Automations",  icon: Sparkle,      description: "Chat, routing & recordings" },
  { id: "drawing-eval",    label: "Drawing Evaluation", icon: Scales,      description: "Compliance & standards checks" },
  { id: "integrations",    label: "Integrations",      icon: Plugs,        description: "Microsoft, Xero & more" },
  { id: "account",         label: "Account & Settings", icon: GearSix,     description: "Profile, security & billing" },
];

const FAQS = [
  {
    id: "gs-1",
    category: "getting-started",
    question: "How do I onboard a new project?",
    answer: "Click \"+ New Project\" and complete the 4 setup screens: (1) Scope — select the folder layout (Split by sub-contractor), then enter the project name and description. (2) Financials & Quoting — enter the quoted price, PO number, and client emails, then click \"+ Add line item\" to break down initial costs such as drafting, sub-consultants, and travel. (3) Contractors — add sub-consultant emails for disciplines such as Architectural, Fire, and Structural to enable automatic routing into their folders. (4) Tags & Notes — add searchable tags such as \"NCC 2022\" and any site notes. Then click \"Create Project\".",
  },
  {
    id: "gs-2",
    category: "getting-started",
    question: "Can multiple people access the same workspace?",
    answer: "Yes. Engineering Hub is multi-tenant — invite teammates from Settings and assign roles so they only see the projects and modules they need.",
  },
  {
    id: "gs-3",
    category: "getting-started",
    question: "Where do I connect my Microsoft account?",
    answer: "Head to Settings → Integrations and click \"Connect Microsoft\". This links OneDrive so files sync directly into your project folders.",
  },
  {
    id: "gs-4",
    category: "getting-started",
    question: "What do I see on the main Projects dashboard?",
    answer: "A real-time summary of all your jobs: Total (every project package ever created), Active (jobs currently in progress), On Hold (paused for client feedback or missing details), and Completed (delivered jobs). Filter instantly using the status tabs, or search by project name, client, address or tag.",
  },
  {
    id: "pf-1",
    category: "projects-files",
    question: "How does the folder structure work?",
    answer: "Every project gets a standard structure (Internal Review, Admin, Transmittal, etc.) with contractor sub-folders for Architectural, Hydraulic, Electrical, Structural and Fire — plus any custom contractors you add.",
  },
  {
    id: "pf-2",
    category: "projects-files",
    question: "Can I explore files without opening OneDrive directly?",
    answer: "Yes — use Explore Files in the sidebar to browse, preview, and manage your synced project files from inside the portal without opening OneDrive directly.",
  },
  {
    id: "pf-auto-routing",
    category: "projects-files",
    question: "How does automated email routing work?",
    answer: "Any email or attachment sent by a client or contractor contact listed on a project is automatically routed straight into that project's OneDrive folder.",
  },
  {
    id: "pf-smart-upload",
    category: "projects-files",
    question: "How does Smart Upload work?",
    answer: "Go to \"Explore Files\" and click \"Smart Upload\". Drag in any PDF or CAD file, and the AI detects the project name and files the document into the correct folder automatically.",
  },
  {
    id: "pf-3",
    category: "projects-files",
    question: "What happens when a drawing revision arrives by email?",
    answer: "The AI router reads incoming emails, matches them to a project and contractor folder, and files the new drawing version automatically — pausing to ask you only if it isn't sure. Any email or attachment from a contact listed on the project is auto-filed straight into that project's OneDrive folder.",
  },
  {
    id: "pf-4",
    category: "projects-files",
    question: "Can I search for files using plain language?",
    answer: "Yes — open Chat in the sidebar and type a plain-language prompt like \"Find the 2024 evacuation plan for 45 Commercial Road\" or \"Show drawings from Mark\", and the AI assistant will locate the matching file for you.",
  },
  {
    id: "pf-5",
    category: "projects-files",
    question: "Can I track site risks, tasks and missing documents on a project?",
    answer: "Inside a project, scroll to Project Management and use the Risks, Issues, and Tasks & WBS tabs (\"+ Add risk\" / \"+ Add task\"). Click \"Update from Project Data\" to let AI scan incoming project emails and automatically add action items or flag risks, or \"AI Deep Search\" to scan your cloud drives for missing floor plans or compliance certificates. \"Generate Report\" exports active risks, tasks and site updates into a shareable PDF.",
  },
  {
    id: "fi-1",
    category: "financials",
    question: "How is the effective hourly rate calculated?",
    answer: "It's net revenue (invoiced amount minus recorded costs) divided by logged hours for that project, shown on the Financials page.",
  },
  {
    id: "fi-2",
    category: "financials",
    question: "What is the \"Hide $\" button?",
    answer: "The \"Hide $\" button at the top right masks all financial figures on your screen, allowing you to share your display with clients or present in public without showing dollar amounts.",
  },
  {
    id: "fi-3",
    category: "financials",
    question: "How do I send an invoice?",
    answer: "Open the project and confirm that its Purchase Order number has been entered. Change the status badge at the top left from \"Active\" to \"Ready For Invoice\". If Xero is connected, this builds the tax invoice in Xero, emails it to the client, and updates your dashboard automatically.",
  },
  {
    id: "fi-4",
    category: "financials",
    question: "What can I track on the Financials analytics page?",
    answer: "Avg / Month Invoiced (billed monthly average), Avg / Month Net (billed income minus cost line items, i.e. profit), Pipeline Value (dollar value of active/quoted jobs not yet invoiced), and a Net Invoiced Over Time graph showing your month-by-month profit trend.",
  },
  {
    id: "fi-5",
    category: "financials",
    question: "How do I manage purchase orders and update job costs?",
    answer: "Type the client's reference into the Purchase Order field on the project's details panel, then use \"Request PO\" or toggle \"PO Requested\" under Financial Summary to track approval. Click \"Edit\" to adjust your overall quote, or scroll to Cost Breakdown and click \"+ Add Item\" to log new drafting/site costs — profit margin is recalculated automatically.",
  },
  {
    id: "ai-1",
    category: "ai-automations",
    question: "What can the AI assistant do inside chat?",
    answer: "It can create and update projects, change statuses, summarise recordings, find files with plain-language prompts (e.g. \"Find the 2024 evacuation plan for 45 Commercial Road\"), and answer questions about your data — just ask from the Chat page.",
  },
  {
    id: "ai-2",
    category: "ai-automations",
    question: "How do I review AI-learned sender emails?",
    answer: "Open AI Automations to see the training log of every sender email the router has learned to associate with a project or contractor.",
  },
  {
    id: "ai-3",
    category: "ai-automations",
    question: "How do I record site walk-throughs or phone calls?",
    answer: "Go to \"Projects Record\" in the sidebar and select the job from the Project menu. Click \"Start Recording\", speak your notes aloud, and click \"Stop\" when finished.",
  },
  {
    id: "ai-4",
    category: "ai-automations",
    question: "Where do my recording summaries go?",
    answer: "The audio is automatically transcribed and summarised into key action items, then attached directly to the project's activity record.",
  },
  {
    id: "de-1",
    category: "drawing-eval",
    question: "How do I check a draft plan against fire safety standards?",
    answer: "Go to \"Drawing Evaluation\" and click \"From Computer\" or \"From OneDrive\" to load your PDF draft. Select \"In-depth\" for regulatory checks or \"Fast\" for layout checks, then click \"Evaluate Against Standards\".",
  },
  {
    id: "de-ai-checks",
    category: "drawing-eval",
    question: "What does the AI check during Drawing Evaluation?",
    answer: "For regulatory compliance, the AI flags missing extinguishers, hydrants, or hose reels. For layout, it detects non-compliant exit path widths, an incorrect \"You Are Here\" orientation, or undersized text.",
  },
  {
    id: "de-2",
    category: "drawing-eval",
    question: "Which file formats are supported for evaluation?",
    answer: "PDF drawings are supported natively, with an in-app viewer so you can see flagged issues directly on the page.",
  },
  {
    id: "de-3",
    category: "drawing-eval",
    question: "Can I compare drawing revisions?",
    answer: "Yes — click \"Compare Drawings\" at the top menu to overlay an old and new version of a drawing and visually highlight what changed between them.",
  },
  {
    id: "int-1",
    category: "integrations",
    question: "Which third-party services can I connect?",
    answer: "Microsoft OneDrive for file storage, Xero for accounting, plus email routing via Azure Logic Apps — all managed from Settings → Integrations.",
  },
  {
    id: "int-2",
    category: "integrations",
    question: "What happens if I disconnect an integration?",
    answer: "Existing synced files and records stay untouched, but new activity will stop syncing until you reconnect the integration.",
  },
  {
    id: "acc-1",
    category: "account",
    question: "How do I reset my password?",
    answer: "Use \"Forgot password\" on the login screen, or set a new one anytime from Settings → Account while logged in.",
  },
  {
    id: "acc-2",
    category: "account",
    question: "Can I permanently delete my account and data?",
    answer: "Yes — Settings → Account has a \"Wipe Account\" option that permanently removes your projects, files and data. This cannot be undone.",
  },
  {
    id: "gd-pm-1",
    category: "projects-files",
    question: "How do I create and set up a new project?",
    answer: "Every project starts as a name, client and description, and instantly gets a full OneDrive folder structure — contractor sub-folders, transmittal register, and more — tailored to the disciplines you choose. Click \"+ New Project\" and complete the 4-step wizard: Scope (folder layout & name), Financials & Quoting (quoted price, PO number, client emails, cost line items), Contractors (sub-consultant emails so their mail auto-routes later), and Tags & Notes.",
  },
  {
    id: "gd-pm-2",
    category: "projects-files",
    question: "How do I track a project's status through its lifecycle?",
    answer: "Every project moves through a clear pipeline — Active, On Hold, Ready for Invoice, Invoice Sent, Invoice Paid, and finally Completed. Marking a project \"Completed\" locks it read-only to protect the record. Change the status dropdown at the top of a project's detail page — some transitions (like \"Ready for Invoice\") automatically trigger other actions, such as Xero invoicing.",
  },
  {
    id: "gd-pm-3",
    category: "projects-files",
    question: "How do I track risks, issues and tasks on a project?",
    answer: "Each project has its own Risks, Issues, and Tasks & WBS tracker so nothing falls through the cracks on-site. Open a project and scroll to \"Project Management.\" Click \"+ Add risk\" or \"+ Add task\" to log one manually, or click \"Update from Project Data\" to let AI scan recent project emails and add items automatically.",
  },
  {
    id: "gd-pm-4",
    category: "projects-files",
    question: "How does the AI find missing documents on a project?",
    answer: "The AI can scan a project's OneDrive folder and compare it against what a project like this would normally need — e.g. a signed contract, site plans, or compliance certificates. On a project's page, click \"AI Deep Search\" to run the missing-document scan, or review the onboarding checklist that appears on new projects.",
  },
  {
    id: "gd-pm-5",
    category: "projects-files",
    question: "How do I generate a client-ready report?",
    answer: "Summarise a project's current risks, tasks and site updates into a polished, shareable document — handy for client check-ins. Click \"Generate Report\" inside a project to export a PDF snapshot of its current status.",
  },
  {
    id: "gd-of-1",
    category: "projects-files",
    question: "How does automatic email and drawing filing work end-to-end?",
    answer: "Any email or attachment sent by a contact listed on a project (client or contractor) is read by the AI, matched to the right project and contractor discipline, and filed into the correct OneDrive folder — including detecting new drawing revisions and archiving the old version. This is fully automatic once a project is set up with client/contractor email addresses — you don't have to trigger it. Occasionally the AI isn't confident which project or folder an email belongs to; when that happens it pauses and raises a question in the Notifications tray, so check Notifications every few days and answer these prompts.",
  },
  {
    id: "gd-of-2",
    category: "projects-files",
    question: "Can I browse and manage files without opening OneDrive?",
    answer: "Explore Files gives you a full file browser — list or grid view, breadcrumbs, rename/move/delete/download — right inside the app. Click \"Explore Files\" in the sidebar. Drag and drop to move files between folders, or use the kebab menu on any file or folder for more actions.",
  },
  {
    id: "gd-of-3",
    category: "projects-files",
    question: "What does Smart Upload detect when I drop in a file?",
    answer: "Drop in any PDF or CAD file and the AI works out which project and folder it belongs to, instead of you filing it manually. Go to Explore Files → \"Smart Upload,\" then drag in your file(s) — or drop them anywhere on the Explore Files page.",
  },
  {
    id: "gd-of-4",
    category: "projects-files",
    question: "Can I find files by describing them in plain language?",
    answer: "Instead of digging through folders, describe what you're looking for and the AI Assistant will locate it, wherever it lives across your projects. Open Chat in the sidebar and type something like \"Find the 2024 evacuation plan for 45 Commercial Road\" or \"Show me drawings from Mark.\"",
  },
  {
    id: "gd-of-5",
    category: "projects-files",
    question: "Can I import an existing OneDrive folder into a new project?",
    answer: "If you already have files organised in OneDrive, the AI can classify and copy them straight into a new project's folder structure instead of you dragging everything manually. When creating a project, toggle \"Import from existing OneDrive folder\" and select the source folder(s).",
  },
  {
    id: "gd-fb-1",
    category: "financials",
    question: "What can I see on the Financials page at a glance?",
    answer: "The Financials page tracks Avg / Month Invoiced, Avg / Month Net (profit), Pipeline Value (quoted but not yet invoiced), and a month-by-month profit trend graph. Open \"Financials\" in the sidebar, under Projects.",
  },
  {
    id: "gd-fb-2",
    category: "financials",
    question: "How do I track purchase orders and project costs?",
    answer: "Log a client's PO reference, track its approval, and break your project costs down line-by-line (drafting, sub-consultants, travel) — profit margin recalculates automatically. Inside a project, enter the PO number in the details panel and toggle \"PO Requested.\" Use \"Cost Breakdown → + Add Item\" to log expenses.",
  },
  {
    id: "gd-fb-3",
    category: "financials",
    question: "How do I hide dollar amounts when presenting to a client?",
    answer: "Mask every currency figure on screen in one click so you can safely share your screen with a client without revealing your numbers. Click \"Hide $\" in the top-right corner of the Projects or Financials page.",
  },
  {
    id: "gd-fb-4",
    category: "financials",
    question: "How does automatic invoicing with Xero work?",
    answer: "Once Xero is connected, moving a project to \"Ready for Invoice\" automatically creates (or updates) the matching invoice in Xero and emails it to the client. When Xero marks it paid, your project updates automatically too. Connect Xero from Settings → Integrations, make sure the project's PO number and quoted price are set, then change its status to \"Ready for Invoice.\"",
  },
  {
    id: "gd-cd-1",
    category: "drawing-eval",
    question: "How do I evaluate a drawing against fire safety and layout standards?",
    answer: "Upload a technical drawing (PDF) and the AI checks it for regulatory compliance (e.g. missing extinguishers, hydrants, hose reels) and layout issues (exit path widths, \"You Are Here\" orientation, undersized text). Go to Drawing Evaluation → load a file \"From Computer\" or \"From OneDrive,\" choose a scan mode (In-depth for regulatory checks, Fast for layout only), then click \"Evaluate Against Standards.\"",
  },
  {
    id: "gd-cd-2",
    category: "drawing-eval",
    question: "How do I compare two drawing revisions visually?",
    answer: "Overlay an old and new version of the same drawing to visually see exactly what changed between revisions. Click \"Compare Drawings\" from the top menu of the Drawing Evaluation page.",
  },
  {
    id: "gd-ai-1",
    category: "ai-automations",
    question: "What can I ask the AI Assistant in Chat?",
    answer: "A general-purpose assistant that can create/update projects, change statuses, find files, summarise recordings, review notifications, and guide you through resolving pending email or drawing decisions in plain language. Click \"Chat\" in the sidebar — it's also available as a floating widget from anywhere in the app. Ask \"Walk me through my notifications\" to review and clear them one at a time.",
  },
  {
    id: "gd-ai-2",
    category: "ai-automations",
    question: "How do I review what the AI router has learned?",
    answer: "Every sender email the router has learned to associate with a project or contractor is logged so you can audit or correct it. Open \"AI Automations\" to view the training log.",
  },
  {
    id: "gd-ai-3",
    category: "ai-automations",
    question: "How do I record and transcribe site meetings?",
    answer: "Capture a site walkthrough or phone call, and the AI automatically transcribes it and summarises it into key action items attached to the project. Go to \"Record\" in the sidebar, select the project, click \"Start Recording,\" then \"Stop\" when finished.",
  },
];

// ─── Page ───────────────────────────────────────────────────────────────────

export default function HelpPage() {
  const [searchQuery, setSearchQuery]     = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const faqSectionRef = useRef(null);

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const item of FAQS) counts[item.category] = (counts[item.category] || 0) + 1;
    return counts;
  }, []);

  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return FAQS.filter((item) => {
      const matchesCategory = activeCategory === "all" || item.category === activeCategory;
      const matchesQuery = !q || item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [searchQuery, activeCategory]);

  const handleCategoryClick = (id) => {
    setActiveCategory(id);
    faqSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const clearFilters = () => {
    setActiveCategory("all");
    setSearchQuery("");
  };

  return (
    <div className="flex-1 overflow-y-auto bg-white" data-testid="help-page">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-6 py-5" data-testid="help-hero">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Help &amp; Guide
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Search FAQs or browse by category below.
            </p>
          </div>

          <div className="relative w-full sm:w-72 md:w-80">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              data-testid="help-search-input"
              className="w-full bg-white pl-9 pr-9 py-2 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                data-testid="help-search-clear-btn"
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-slate-50/50">
        <div className="max-w-5xl mx-auto px-4 py-8 sm:px-8 sm:py-10 space-y-10">
          {/* Category grid */}
          <section>
            <p className="text-[10px] font-semibold tracking-[0.15em] text-slate-400 uppercase mb-3">
              Browse by category
            </p>
            <CategoryCards
              categories={CATEGORIES}
              counts={categoryCounts}
              totalCount={FAQS.length}
              activeCategory={activeCategory}
              onCategoryClick={handleCategoryClick}
            />
          </section>

          {/* FAQ container */}
          <section ref={faqSectionRef} data-testid="help-faq-section">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold tracking-[0.15em] text-slate-400 uppercase">
                Frequently asked questions
              </p>
              {(activeCategory !== "all" || searchQuery) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  data-testid="help-clear-filters-btn"
                  className="text-xs font-medium text-primary hover:text-primary-hover flex items-center gap-1"
                >
                  <X size={12} /> Clear filters
                </button>
              )}
            </div>

            <div className="bg-white border border-slate-200" data-testid="help-faq-container">
              {filteredFaqs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-2" data-testid="help-faq-empty-state">
                  <Lifebuoy size={24} className="text-slate-300" />
                  <p className="text-sm text-slate-500">No results found. Try a different search or category.</p>
                </div>
              ) : (
                <Accordion type="single" collapsible className="px-5">
                  {filteredFaqs.map((item) => (
                    <AccordionItem key={item.id} value={item.id} data-testid={`help-faq-item-${item.id}`}>
                      <AccordionTrigger
                        data-testid={`help-faq-question-${item.id}`}
                        className="font-cabinet text-sm font-bold text-slate-900 hover:no-underline"
                      >
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent
                        data-testid={`help-faq-answer-${item.id}`}
                        className="text-sm text-slate-500 leading-relaxed"
                      >
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
